import React from "react";
import clsx from "clsx";

import { ReactComponent as IconUser } from "../../assets/images/AnswerList/icon_user.svg";
import { IAnswerR, IStudent } from "../../state";
import { generateFullName } from "../../libs/Util";
import OneAnswerBox from "../OneAnswerBox";

import style from "./AnswerBox.module.scss";

export type AnswerBoxProps = {
  student?: IStudent;
  name_opened: boolean;
  answer_opened?: boolean;
  answers: IAnswerR[];
  isMyAnswer?: boolean;
  currentUser?: IStudent;
  calling: boolean;
  onSendComment: (answerId: number, text: string, commentId?: number) => void;
  onDeleteReply: (answerId: number, commentId: number) => void;
  handleLike: (answerId: number) => void;
};

const AnswerBox: React.FC<AnswerBoxProps> = ({
  student,
  name_opened,
  answer_opened,
  answers,
  isMyAnswer,
  onSendComment,
  onDeleteReply,
  currentUser,
  handleLike,
  calling,
}) => {
  const myAnswer = React.useMemo(() => {
    if (!name_opened && answer_opened)
      return answers.find(answer => answer.student.id === currentUser?.id);
  }, [answers, currentUser, name_opened, answer_opened]);

  const answersWrap = React.useMemo(() => {
    if (name_opened || !answer_opened) return answers;
    return answers.filter(answer => answer.student.id !== currentUser?.id);
  }, [answers, currentUser, name_opened, answer_opened]);

  const replyAnswer = React.useCallback(
    (content: string, answerId: number) => {
      if (!content.length) return;
      onSendComment(answerId, content);
    },
    [onSendComment],
  );

  const updateReply = React.useCallback(
    (content: string, answerId: number, commentId?: number) => {
      onSendComment(answerId, content, commentId);
    },
    [onSendComment],
  );

  return (
    <div className={style.answer}>
      {name_opened && student && (
        <div className={style.nameStudent}>
          <span className={clsx(isMyAnswer && style.myAnswer)}>
            <IconUser />
            {generateFullName([student.last_name, student.first_name])}
            {isMyAnswer && ` (あなたの回答)`}
          </span>
        </div>
      )}
      {myAnswer && (
        <>
          {!name_opened && (
            <span className={style.myAnswerNo}>
              {`回答${myAnswer.index} (あなたの回答番号)`}
            </span>
          )}
          <OneAnswerBox
            handleLike={handleLike}
            replyAnswer={replyAnswer}
            updateReply={updateReply}
            name_opened={name_opened}
            onDeleteReply={onDeleteReply}
            answer={myAnswer}
            isMyAnswer
            currentUser={currentUser}
            calling={calling}
          />
        </>
      )}
      {answersWrap.length > 0 &&
        answersWrap.map(answer => (
          <React.Fragment key={answer.id}>
            {!name_opened && (
              <span className={style.answerNo}>{`回答${answer.index}`}</span>
            )}
            <OneAnswerBox
              handleLike={handleLike}
              replyAnswer={replyAnswer}
              updateReply={updateReply}
              name_opened={name_opened}
              onDeleteReply={onDeleteReply}
              answer={answer}
              isMyAnswer={isMyAnswer}
              currentUser={currentUser}
              calling={calling}
            />
          </React.Fragment>
        ))}
      {!myAnswer && answersWrap.length === 0 && (
        <p className={style.emptyMessageBox}>まだ回答がありません。</p>
      )}
    </div>
  );
};

export default AnswerBox;
